// src/components/team/LotsCsvUploader.jsx
// Mini uploader CSV pour les lots (auction_lots)
// ⚠️ Nécessite : npm i papaparse
// Usage: <LotsCsvUploader saleId={saleId} onDone={() => reload()} />

import React, { useRef, useState } from "react";
import Papa from "papaparse";
import { supabase } from "../../lib/supabaseClient";

const REQUIRED_HEADERS = [
  "lot_number",
  "artist_name",
  "title",
  "year",
  "medium",
  "width_cm",
  "height_cm",
  "estimate_low",
  "estimate_high",
  "hammer_price",
  "price_with_fees",
  "currency",
  "result_status",
  "source_url",
];

function parseNum(v) {
  if (v === null || v === undefined) return null;
  if (typeof v !== "string") return Number(v) || null;
  const s = v.replace(/\s/g, "").replace(/[€$£]/g, "").replace(/,/g, ".");
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

export default function LotsCsvUploader({ saleId, onDone }) {
  const fileRef = useRef(null);
  const [rows, setRows] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [status, setStatus] = useState("");
  const [inserting, setInserting] = useState(false);

  const tplLink = URL.createObjectURL(
    new Blob(
      [
        [
          "lot_number",
          "artist_name",
          "title",
          "year",
          "medium",
          "width_cm",
          "height_cm",
          "estimate_low",
          "estimate_high",
          "hammer_price",
          "price_with_fees",
          "currency",
          "result_status",
          "source_url",
        ].join(","),
        "\n",
        [
          "12",
          "Cindy Sherman",
          "Untitled #96",
          "1981",
          "Chromogenic print",
          "61",
          "50.8",
          "80000",
          "120000",
          "95000",
          "118750",
          "EUR",
          "sold",
          "https://auction-house.example/sale/L123/lot/12",
        ].join(","),
      ].join("")
    ),
    { type: "text/csv" }
  );

  function handleFile(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    setStatus("Lecture du fichier…");
    Papa.parse(f, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (h) => h.trim().toLowerCase(),
      delimiter: ",", // Papa détecte automatiquement ; ou , si besoin
      complete: (res) => {
        const rows = res.data?.map((r) => {
          const rr = {};
          Object.keys(r || {}).forEach((k) => {
            rr[k.trim().toLowerCase()] =
              typeof r[k] === "string" ? r[k].trim() : r[k];
          });
          return rr;
        }) || [];
        const hdrs = res.meta?.fields?.map((h) => h.trim().toLowerCase()) || [];
        setHeaders(hdrs);
        setRows(rows);
        setStatus(`Fichier chargé : ${rows.length} lignes.`);
      },
      error: (err) => setStatus(`❌ Erreur de lecture : ${err.message}`),
    });
  }

  function validateHeaders() {
    const missing = REQUIRED_HEADERS.filter((h) => !headers.includes(h));
    return { ok: missing.length === 0, missing };
  }

  async function insertAll() {
    if (!saleId) {
      setStatus("❌ saleId manquant : crée d'abord la session de vente.");
      return;
    }
    const { ok, missing } = validateHeaders();
    if (!ok) {
      setStatus("❌ En-têtes manquantes : " + missing.join(", "));
      return;
    }
    if (!rows.length) {
      setStatus("❌ Aucune ligne à insérer.");
      return;
    }

    setInserting(true);
    setStatus("Insertion en cours…");

    const mapped = rows.map((r) => ({
      sale_id: saleId,
      lot_number: r.lot_number || null,
      artist_name: r.artist_name || null,
      title: r.title || null,
      year: r.year || null,
      medium: r.medium || null,
      width_cm: parseNum(r.width_cm),
      height_cm: parseNum(r.height_cm),
      estimate_low: parseNum(r.estimate_low),
      estimate_high: parseNum(r.estimate_high),
      hammer_price: parseNum(r.hammer_price),
      price_with_fees: parseNum(r.price_with_fees),
      currency: (r.currency || "").toUpperCase() || null,
      result_status: (r.result_status || "").toLowerCase() || null,
      source_url: r.source_url || null,
    }));

    const chunkSize = 200;
    let inserted = 0;
    for (let i = 0; i < mapped.length; i += chunkSize) {
      const chunk = mapped.slice(i, i + chunkSize);
      const { error, count } = await supabase
        .from("auction_lots")
        .insert(chunk, { count: "exact" });
      if (error) {
        setInserting(false);
        setStatus(`❌ Erreur à la ligne ${i + 1}: ${error.message}`);
        return;
      }
      inserted += (count || chunk.length);
      setStatus(
        `Insertion… ${Math.min(i + chunk.length, mapped.length)}/${
          mapped.length
        }`
      );
    }

    setInserting(false);
    setStatus(`✅ ${inserted} lots insérés`);
    if (typeof onDone === "function") onDone();
  }

  const { ok, missing } = validateHeaders();
  const preview = rows.slice(0, 5);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-medium">Importer des lots (CSV)</h3>
          <p className="text-xs text-gray-500">
            En-têtes obligatoires : {REQUIRED_HEADERS.join(", ")}
          </p>
        </div>
        <a
          href={tplLink}
          download="auction_lots_template.csv"
          className="text-sm underline"
        >
          Télécharger le modèle
        </a>
      </div>

      <input
        type="file"
        accept=".csv,text/csv"
        ref={fileRef}
        onChange={handleFile}
        className="block w-full text-sm"
      />

      {rows.length > 0 && (
        <div className="text-xs text-gray-600 space-y-2">
          <div>
            Fichier : {rows.length} lignes.{" "}
            {ok ? (
              <span className="text-green-600">En-têtes OK</span>
            ) : (
              <span className="text-red-600">
                Manques : {missing.join(", ")}
              </span>
            )}
          </div>

          {/* Aperçu rapide des 5 premières lignes */}
          <div className="overflow-x-auto border rounded-md">
            <table className="w-full text-[11px] min-w-[800px]">
              <thead className="bg-gray-50 border-b">
                <tr>
                  {headers.map((h) => (
                    <th key={h} className="px-2 py-1 text-left">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {preview.map((r, i) => (
                  <tr key={i} className="border-t hover:bg-gray-50">
                    {headers.map((h) => (
                      <td key={h} className="px-2 py-1 truncate max-w-[150px]">
                        {r[h] ?? ""}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="flex gap-2">
        <button
          className="px-4 py-2 rounded bg-black text-white disabled:opacity-50"
          onClick={insertAll}
          disabled={inserting || !rows.length}
        >
          {inserting ? "Insertion…" : "Insérer dans la vente"}
        </button>
        {status && <div className="text-sm text-gray-700">{status}</div>}
      </div>
    </div>
  );
}
